"use client"

import { useState, useEffect } from "react"
import { ArrowLeft, Edit3, Plus, Share2, Download, X } from 'lucide-react'
import PlanningMapView from "./PlanningMapView"
import "./PlanningInterface.css"

const PlanningInterface = ({
  places,
  currentListId,
  userId,
  userNickname,
  onBackToMap,
  onBackToVoting,
  API_BASE,
  linkedTripData,
  onTripDataUpdate,
  placeTypes = {}, // Add placeTypes prop
}) => {
  // Load trip data from props or localStorage
  const [tripData, setTripData] = useState(linkedTripData)
  const [selectedDate, setSelectedDate] = useState(null)
  const [currentMode, setCurrentMode] = useState("view")
  const [visiblePlaces, setVisiblePlaces] = useState(places)

  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [showAccommodationModal, setShowAccommodationModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // Edit states
  const [currentEditingPlanInfo, setCurrentEditingPlanInfo] = useState({
    originalDate: null,
    dayIndex: -1,
    planIndex: -1,
  })
  const [currentEditingAccommodationId, setCurrentEditingAccommodationId] = useState(null)

  // Sharing state
  const [isSharedPlan, setIsSharedPlan] = useState(false)
  const [sharedPlanId, setSharedPlanId] = useState(null)
  const [currentActiveLinkId, setCurrentActiveLinkId] = useState(null)
  const [canEdit, setCanEdit] = useState(true)

  const PLAN_ICONS = {
    accommodation: "🏠",
    meal: "🍽️",
    activity: "🎯",
    flight: "✈️",
    drive: "🚗",
    note: "📝",
  }

  // Load trip data when component mounts or linkedTripData changes
  useEffect(() => {
    if (linkedTripData) {
      console.log("📅 Loading linked trip data:", linkedTripData)
      setTripData(linkedTripData)
      setSharedPlanId(linkedTripData.sharedPlanId || null)
      setCurrentActiveLinkId(linkedTripData.currentActiveLinkId || null)
      setIsSharedPlan(!!linkedTripData.sharedPlanId)

      // Set initial selected date
      if (linkedTripData.itinerary && linkedTripData.itinerary.length > 0) {
        const today = new Date().toISOString().split("T")[0]
        const todayInTrip = linkedTripData.itinerary.find((day) => day.date === today)
        setSelectedDate(todayInTrip ? today : linkedTripData.itinerary[0].date)
      }
    } else {
      // Try to load from localStorage as fallback
      loadTripDataFromStorage()
    }
  }, [linkedTripData])

  // Update visible places when places prop changes
  useEffect(() => {
    setVisiblePlaces(places)
  }, [places])

  const loadTripDataFromStorage = () => {
    try {
      const data = localStorage.getItem("tripCalendarData_v3")
      if (data) {
        const loaded = JSON.parse(data)
        console.log("📅 Loaded trip data from localStorage:", loaded)
        setTripData(loaded)
        setSharedPlanId(loaded.sharedPlanId || null)
        setCurrentActiveLinkId(loaded.currentActiveLinkId || null)
        setIsSharedPlan(!!loaded.sharedPlanId)

        // Set initial selected date
        if (loaded.itinerary && loaded.itinerary.length > 0) {
          const today = new Date().toISOString().split("T")[0]
          const todayInTrip = loaded.itinerary.find((day) => day.date === today)
          setSelectedDate(todayInTrip ? today : loaded.itinerary[0].date)
        }
      }
    } catch (error) {
      console.error("Error loading trip data from localStorage:", error)
    }
  }

  // Save trip data to localStorage and notify parent
  const saveTripData = async (updatedTripData) => {
    try {
      // Always save to localStorage first
      localStorage.setItem("tripCalendarData_v3", JSON.stringify(updatedTripData))
      console.log("✅ Trip data saved to localStorage")

      // Update local state
      setTripData(updatedTripData)

      // Notify parent component to save linked data
      if (onTripDataUpdate) {
        await onTripDataUpdate(updatedTripData)
      }

      // If we have a shared plan ID and API_BASE, also save to server
      if (updatedTripData.sharedPlanId && API_BASE) {
        try {
          const response = await fetch(`${API_BASE}/trip-manager.php?action=update`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              planId: updatedTripData.sharedPlanId,
              userId: userId,
              tripData: updatedTripData,
            }),
          })

          const data = await response.json()
          if (data.success) {
            console.log("✅ Trip data synced to server")
          } else {
            console.warn("⚠️ Server sync failed, but local save succeeded")
          }
        } catch (serverError) {
          console.warn("⚠️ Server sync failed, but local save succeeded:", serverError)
        }
      }
    } catch (error) {
      console.error("❌ Error saving trip data:", error)
    }
  }

  // Utility functions
  const formatDateForInput = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const generateId = () => {
    return "id_" + Math.random().toString(36).substring(2, 15)
  }

  // Get accommodation class for styling
  const getAccommodationClass = (day) => {
    if (!day.plans) return "no-accommodation"

    const accommodation = day.plans.find((plan) => plan.type === "accommodation")
    if (!accommodation) return "no-accommodation"

    // Map accommodation names to location classes
    const accommodationName = accommodation.details.toLowerCase()
    if (accommodationName.includes("shari")) return "location-shari"
    if (accommodationName.includes("iowa")) return "location-iowa"
    if (accommodationName.includes("bergstrom")) return "location-bergstrom"
    if (accommodationName.includes("ross")) return "location-ross"
    return "location-other"
  }

  // Check if date is today
  const isToday = (dateStr) => {
    const today = new Date().toISOString().split("T")[0]
    return dateStr === today
  }

  // Handle planning places (called from map)
  const handlePlanPlaces = (placesToPlan, targetDate) => {
    console.log("🎯 handlePlanPlaces called!", {
      placesToPlanCount: placesToPlan.length,
      targetDate,
      tripData: !!tripData,
    })

    const dateToUse = targetDate || selectedDate

    if (!tripData || !dateToUse || placesToPlan.length === 0) {
      console.log("❌ Cannot plan places: missing data")
      alert("Please select a date and ensure there are places to plan.")
      return
    }

    // Create a deep copy to ensure state updates
    const updatedTripData = JSON.parse(JSON.stringify(tripData))
    const dayIndex = updatedTripData.itinerary.findIndex((day) => day.date === dateToUse)

    if (dayIndex === -1) {
      console.log("❌ Selected date not found in itinerary")
      alert("Selected date not found in trip itinerary.")
      return
    }

    let addedCount = 0

    // Add each place as an activity
    placesToPlan.forEach((place, index) => {
      const newActivity = {
        type: "activity",
        details: `Visit ${place.name}`,
        time: `${String(10 + index).padStart(2, "0")}:00`,
        confirmed: false,
      }

      // Check if this activity already exists
      const existingActivity = updatedTripData.itinerary[dayIndex].plans.find(
        (plan) => plan.type === "activity" && plan.details === newActivity.details,
      )

      if (!existingActivity) {
        updatedTripData.itinerary[dayIndex].plans.push(newActivity)
        addedCount++
      }
    })

    // Sort plans by time
    updatedTripData.itinerary[dayIndex].plans.sort((a, b) => {
      if (a.type === "accommodation" && b.type !== "accommodation") return -1
      if (a.type !== "accommodation" && b.type === "accommodation") return 1
      return (a.time || "23:59").localeCompare(b.time || "23:59")
    })

    saveTripData(updatedTripData)

    if (addedCount > 0) {
      alert(`✅ Added ${addedCount} new activities to ${formatDateForDisplay(dateToUse).fullDate}!`)
    } else {
      alert(`ℹ️ All ${placesToPlan.length} places were already planned for this day.`)
    }
  }

  // Add/Edit plan
  const savePlan = (planData) => {
    console.log("💾 Saving plan:", planData)
    const { dayIndex, planIndex } = currentEditingPlanInfo
    const { newDate, type, time, details, confirmed } = planData

    if (!newDate || !type || !details) {
      alert("Date, Type, and Details are required.")
      return
    }

    const plan = { type, time, details, confirmed }
    const updatedTripData = JSON.parse(JSON.stringify(tripData))

    if (planIndex === -1) {
      // Adding new plan
      const targetDayIndex = updatedTripData.itinerary.findIndex((d) => d.date === newDate)
      if (targetDayIndex === -1) {
        alert("Cannot add plan to a non-existent day.")
        return
      }

      updatedTripData.itinerary[targetDayIndex].plans.push(plan)
      updatedTripData.itinerary[targetDayIndex].plans.sort((a, b) => {
        if (a.type === "accommodation" && b.type !== "accommodation") return -1
        if (a.type !== "accommodation" && b.type === "accommodation") return 1
        return (a.time || "23:59").localeCompare(b.time || "23:59")
      })
    } else {
      // Editing existing plan
      updatedTripData.itinerary[dayIndex].plans[planIndex] = plan
      updatedTripData.itinerary[dayIndex].plans.sort((a, b) => {
        if (a.type === "accommodation" && b.type !== "accommodation") return -1
        if (a.type !== "accommodation" && b.type === "accommodation") return 1
        return (a.time || "23:59").localeCompare(b.time || "23:59")
      })
    }

    saveTripData(updatedTripData)
    setShowPlanModal(false)
    setCurrentEditingPlanInfo({ originalDate: null, dayIndex: -1, planIndex: -1 })
  }

  // Delete plan
  const deletePlan = () => {
    setConfirmAction(() => () => {
      const { dayIndex, planIndex } = currentEditingPlanInfo
      const updatedTripData = JSON.parse(JSON.stringify(tripData))
      updatedTripData.itinerary[dayIndex].plans.splice(planIndex, 1)
      saveTripData(updatedTripData)
      setShowPlanModal(false)
      setCurrentEditingPlanInfo({ originalDate: null, dayIndex: -1, planIndex: -1 })
      setShowConfirmDialog(false)
    })
    setShowConfirmDialog(true)
  }

  // Open plan modal for editing
  const openPlanModal = (dayIndex = -1, planIndex = -1, date = selectedDate) => {
    setCurrentEditingPlanInfo({
      originalDate: date,
      dayIndex,
      planIndex,
    })
    setShowPlanModal(true)
  }

  // Handle map bounds change
  const handleMapBoundsChange = (newVisiblePlaces) => {
    setVisiblePlaces(newVisiblePlaces)
  }

  const formatDateForDisplay = (dateStr) => {
    const date = new Date(dateStr + "T12:00:00")
    return {
      weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
      day: date.getDate(),
      fullDate: date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      }),
      year: date.getFullYear(),
    }
  }

  if (!tripData || !tripData.itinerary || tripData.itinerary.length === 0) {
    return (
      <div className="planning-interface">
        <div className="planning-header">
          <button onClick={onBackToMap} className="back-btn">
            <ArrowLeft size={24} />
          </button>
          <h1>No Trip Data</h1>
        </div>
        <div className="no-trip-message">
          <p>No trip data found. Please create or import a trip first using the calendar button.</p>
        </div>
      </div>
    )
  }

  const selectedDay = tripData.itinerary.find((day) => day.date === selectedDate)

  return (
    <div className="planning-interface">
      {/* Header */}
      <div className="planning-header">
        <button onClick={onBackToMap} className="back-btn">
          <ArrowLeft size={24} />
        </button>
        <h1 className="trip-title">{tripData.calendarName || "Trip Plan"}</h1>
        <div className="header-actions">
          <button
            className={`mode-btn ${currentMode === "view" ? "active" : ""}`}
            onClick={() => setCurrentMode("view")}
            title="View Mode"
          >
            👁️
          </button>
          <button
            className={`mode-btn ${currentMode === "edit" ? "active" : ""}`}
            onClick={() => setCurrentMode("edit")}
            disabled={!canEdit}
            title="Edit Mode"
          >
            <Edit3 size={16} />
          </button>
          <button onClick={() => setShowShareModal(true)} className="action-btn" title="Share">
            <Share2 size={16} />
          </button>
        </div>
      </div>

      {/* Enhanced Date Carousel with accommodation styling */}
      <div className="date-carousel">
        {tripData.itinerary.map((day) => {
          const dateInfo = formatDateForDisplay(day.date)
          const isSelected = selectedDate === day.date
          const accommodationClass = getAccommodationClass(day)
          const todayClass = isToday(day.date) ? "is-today" : ""

          return (
            <button
              key={day.date}
              className={`date-item ${accommodationClass} ${isSelected ? "selected" : ""} ${todayClass}`}
              onClick={() => setSelectedDate(day.date)}
            >
              <div className="date-weekday">{dateInfo.weekday.toUpperCase()}</div>
              <div className="date-number">{dateInfo.day}</div>
              <div className={`date-indicator ${accommodationClass}`}></div>
            </button>
          )
        })}
      </div>

      {/* Map Section - now with placeTypes */}
      <div className="map-section">
        <PlanningMapView
          places={places}
          onBoundsChange={handleMapBoundsChange}
          onPlanPlaces={handlePlanPlaces}
          selectedDate={selectedDate}
          placeTypes={placeTypes} // Pass placeTypes to map
        />
      </div>

      {/* Day Details */}
      {selectedDay && (
        <div className="day-details">
          <div className="day-header">
            <h2 className="day-title">{formatDateForDisplay(selectedDay.date).fullDate}</h2>
            <span className="day-date">
              {formatDateForDisplay(selectedDay.date).fullDate}, {formatDateForDisplay(selectedDay.date).year}
            </span>
          </div>

          <div className="plans-list">
            {selectedDay.plans && selectedDay.plans.length > 0 ? (
              selectedDay.plans.map((plan, index) => (
                <div
                  key={index}
                  className={`plan-item ${plan.type}`}
                  onClick={() => {
                    if (currentMode === "edit") {
                      openPlanModal(
                        tripData.itinerary.findIndex((d) => d.date === selectedDate),
                        index,
                        selectedDate,
                      )
                    }
                  }}
                  style={{ cursor: currentMode === "edit" ? "pointer" : "default" }}
                >
                  <div className="plan-icon">{PLAN_ICONS[plan.type] || "📌"}</div>
                  <div className="plan-content">
                    <div className="plan-header">
                      <span className="plan-time">{plan.time || "All Day"}</span>
                      {plan.confirmed && <span className="plan-status confirmed">✅ Confirmed</span>}
                    </div>
                    <h3 className="plan-title">{plan.details}</h3>
                    <p className="plan-type">{plan.type.charAt(0).toUpperCase() + plan.type.slice(1)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-plans">
                <p>No plans for this day yet.</p>
                {currentMode === "edit" && (
                  <button className="add-plan-btn" onClick={() => openPlanModal(-1, -1, selectedDate)}>
                    <Plus size={16} />
                    Add your first plan
                  </button>
                )}
              </div>
            )}

            {currentMode === "edit" && selectedDay.plans.length > 0 && (
              <div className="add-plan-buttons">
                <button className="btn btn-outline" onClick={() => openPlanModal(-1, -1, selectedDate)}>
                  <Plus size={16} />
                  Add Plan
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom Actions */}
      <div className="bottom-actions">
        <button onClick={onBackToMap} className="action-btn secondary">
          ← Back to Map View
        </button>
      </div>

      {/* Modals */}
      {showPlanModal && <PlanModal />}
      {showShareModal && <ShareModal />}
      {showConfirmDialog && <ConfirmDialog />}
    </div>
  )

  // Plan Modal Component
  function PlanModal() {
    const { dayIndex, planIndex } = currentEditingPlanInfo
    const isEditing = planIndex !== -1
    const existingPlan = isEditing ? tripData.itinerary[dayIndex]?.plans[planIndex] : null

    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>{isEditing ? "Edit Plan" : "Add Plan"}</h3>
            <button onClick={() => setShowPlanModal(false)} className="close-btn">
              <X size={20} />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const formData = new FormData(e.target)
              savePlan({
                newDate: formData.get("date"),
                type: formData.get("type"),
                time: formData.get("time"),
                details: formData.get("details"),
                confirmed: formData.get("confirmed") === "on",
              })
            }}
          >
            <div className="modal-content">
              <div className="form-group">
                <label>Date:</label>
                <select name="date" required defaultValue={selectedDate}>
                  {tripData.itinerary.map((day) => (
                    <option key={day.date} value={day.date}>
                      {day.displayDate || formatDateForDisplay(day.date).fullDate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type:</label>
                  <select name="type" required defaultValue={existingPlan?.type || "activity"}>
                    <option value="accommodation">🏠 Accommodation</option>
                    <option value="meal">🍽️ Meal</option>
                    <option value="activity">🎯 Activity</option>
                    <option value="flight">✈️ Flight</option>
                    <option value="drive">🚗 Drive</option>
                    <option value="note">📝 Note</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Time:</label>
                  <input type="time" name="time" defaultValue={existingPlan?.time || "10:00"} />
                </div>
              </div>
              <div className="form-group">
                <label>Details:</label>
                <textarea
                  name="details"
                  required
                  rows={3}
                  placeholder="Enter plan details..."
                  defaultValue={existingPlan?.details || ""}
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" name="confirmed" defaultChecked={existingPlan?.confirmed || false} />
                  Confirmed
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <div className="modal-footer-left">
                {isEditing && (
                  <button type="button" onClick={deletePlan} className="btn btn-danger">
                    Delete
                  </button>
                )}
              </div>
              <div className="modal-footer-right">
                <button type="button" onClick={() => setShowPlanModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {isEditing ? "Update" : "Add"} Plan
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Share Modal Component
  function ShareModal() {
    const [exportData, setExportData] = useState("")
    const [showExportData, setShowExportData] = useState(false)

    const generateExportData = () => {
      const data = {
        calendarName: tripData.calendarName,
        itinerary: tripData.itinerary,
        accommodations: tripData.accommodations || [],
        tripTimezone: tripData.tripTimezone,
        sharedPlanId,
        currentActiveLinkId,
      }
      const dataStr = JSON.stringify(data, null, 2)
      setExportData(dataStr)
      setShowExportData(true)
    }

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(exportData)
        alert("✅ Trip data copied to clipboard!")
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea")
        textArea.value = exportData
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
        alert("✅ Trip data copied to clipboard!")
      }
    }

    if (showExportData) {
      return (
        <div className="modal-overlay">
          <div className="modal export-modal">
            <div className="modal-header">
              <h3>Export Data</h3>
              <button onClick={() => setShowShareModal(false)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-content export-content">
              <textarea value={exportData} readOnly className="export-textarea" rows={15} />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowExportData(false)} className="btn btn-secondary">
                Close
              </button>
              <button onClick={copyToClipboard} className="btn btn-primary">
                Copy
              </button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>Share & Export</h3>
            <button onClick={() => setShowShareModal(false)} className="close-btn">
              <X size={20} />
            </button>
          </div>
          <div className="modal-content">
            <div className="share-options">
              <button onClick={generateExportData} className="btn btn-primary">
                <Download size={16} />
                Export Trip Data
              </button>
              <p className="help-text">Export your trip data to copy and paste elsewhere or import later.</p>
            </div>
          </div>
          <div className="modal-footer">
            <button onClick={() => setShowShareModal(false)} className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Confirm Dialog Component
  function ConfirmDialog() {
    if (!showConfirmDialog) return null

    return (
      <div className="modal-overlay">
        <div className="modal confirm-modal">
          <div className="modal-header">
            <h3>Confirm Delete</h3>
          </div>
          <div className="modal-content">
            <p>Are you sure you want to delete this plan?</p>
          </div>
          <div className="modal-footer">
            <button onClick={() => setShowConfirmDialog(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button onClick={confirmAction} className="btn btn-danger">
              Delete
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export default PlanningInterface
